import React from 'react'
import { v } from './shared'

// The Sonoran desert at the edge of Tucson: a few mesas in the haze, the
// desert floor, and saguaros out on the flats. The sun sets
// behind the ranges instead of into water, so there is no road on the water.

// The road is built from one smooth centreline (a cubic from the bottom of
// the frame to a vanishing point just below the horizon, right of the sun)
// offset by a width that narrows with distance, so both edges stay continuous.
const ROAD = (() => {
  // Starts below the frame so the slanted end of the shape is never visible.
  // Swings right across the flats, then bends left to meet the horizon in
  // the gap between the sun and the mesa.
  const P = [[280, 580], [350, 430], [575, 372], [510, 304]]
  const at = (t) => {
    const u = 1 - t
    const x = u * u * u * P[0][0] + 3 * u * u * t * P[1][0] + 3 * u * t * t * P[2][0] + t * t * t * P[3][0]
    const y = u * u * u * P[0][1] + 3 * u * u * t * P[1][1] + 3 * u * t * t * P[2][1] + t * t * t * P[3][1]
    const dx = 3 * u * u * (P[1][0] - P[0][0]) + 6 * u * t * (P[2][0] - P[1][0]) + 3 * t * t * (P[3][0] - P[2][0])
    const dy = 3 * u * u * (P[1][1] - P[0][1]) + 6 * u * t * (P[2][1] - P[1][1]) + 3 * t * t * (P[3][1] - P[2][1])
    const len = Math.hypot(dx, dy) || 1
    return { x, y, nx: -dy / len, ny: dx / len }
  }
  const half = (t) => 128 * Math.pow(1 - t, 1.7) + 1.2
  const left = [], right = []
  for (let i = 0; i <= 28; i++) {
    const t = i / 28
    const p = at(t), w = half(t)
    left.push(`${(p.x + p.nx * w).toFixed(1)},${(p.y + p.ny * w).toFixed(1)}`)
    right.push(`${(p.x - p.nx * w).toFixed(1)},${(p.y - p.ny * w).toFixed(1)}`)
  }
  return 'M' + left.join(' L') + ' L' + right.reverse().join(' L') + ' Z'
})()

const Desert = () => (
  <>
    {/* A far range, faint in the haze, behind everything */}
    <path
      d="M-10,300 L20,284 L60,290 L110,270 L150,282 L200,262 L240,278 L290,268 L330,286 L370,276 L410,290 L460,280 L500,292 L560,274 L610,284 L660,266 L710,280 L760,272 L810,286 V300 Z"
      style={{ fill: v('sea1') }}
    />

    {/* Mesas: two far back in the haze, two closer, and open horizon between */}
    <path d="M30,300 L50,278 L92,274 L98,270 L246,270 L252,276 L296,284 L314,300 Z" style={{ fill: v('sea1') }} />
    <path d="M598,300 L610,288 L654,286 L658,283 L726,283 L730,287 L766,292 L780,300 Z" style={{ fill: v('sea1') }} />
    <path d="M530,300 L548,272 L596,268 L602,263 L690,263 L696,269 L732,278 L748,300 Z" style={{ fill: v('sea2') }} opacity="0.95" />
    <path d="M140,300 L152,291 L190,289 L194,287 L262,287 L266,290 L294,295 L304,300 Z" style={{ fill: v('sea2') }} opacity="0.9" />

    {/* The desert floor */}
    <rect x="0" y="300" width="800" height="200" fill="url(#sky-sand)" />
    <g className="sunlit">
      <rect x="0" y="299" width="800" height="1.6" style={{ fill: v('horizon') }} opacity="0.55" />
    </g>
    <rect className="shade" x="0" y="200" width="800" height="220" fill="url(#sky-damp)" />

    {/* A low ridge across the flats */}
    <path d="M-10,332 C80,320 150,330 230,322 C310,314 380,330 460,324 C540,318 620,332 710,326 C760,322 790,326 810,328 V344 H-10 Z" style={{ fill: v('sand1') }} opacity="0.26" />
    {/* A road running out to the horizon, tapering to a point right of the sun */}
    <path d={ROAD} style={{ fill: v('sand0') }} opacity="0.5" />

    {/* Ground contours, drifting slowly like heat over the flats */}
    <g fill="none" style={{ stroke: v('sand1') }} strokeLinecap="round">
      <path className="wave" style={{ animationDuration: '19s' }} d="M40,352 q40,-4 80,0 t80,0" strokeWidth="1.6" opacity="0.28" />
      <path className="wave" style={{ animationDuration: '23s', animationDelay: '-6s' }} d="M520,366 q44,-4 88,0 t88,0" strokeWidth="1.6" opacity="0.24" />
      <path className="wave" style={{ animationDuration: '17s', animationDelay: '-3s' }} d="M240,392 q50,-5 100,0 t100,0" strokeWidth="2" opacity="0.2" />
    </g>

    {/* Saguaros out on the flats, smaller and fainter the farther they stand */}
    <g fill="none" style={{ stroke: v('boat') }} strokeLinecap="round" strokeLinejoin="round" opacity="0.55">
      {[
        // [x, base, height, stroke]  arms are drawn relative to the trunk
        // The middle three sit inside the band a phone shows; the outer ones
        // fill out wide screens. All keep clear of the road.
        [110, 346, 18, 2.4], [236, 340, 16, 2.2], [336, 356, 20, 2.6],
        [588, 334, 12, 1.8], [652, 350, 18, 2.4], [758, 358, 20, 2.6],
      ].map(([x, base, h, w], i) => (
        <g key={i} strokeWidth={w}>
          <path d={`M${x},${base} V${base - h}`} />
          <path d={`M${x - w * 0.4},${base - h * 0.55} H${x - h * 0.28} V${base - h * 0.82}`} strokeWidth={w * 0.78} />
          {i % 3 !== 1 && (
            <path d={`M${x + w * 0.4},${base - h * 0.42} H${x + h * 0.24} V${base - h * 0.7}`} strokeWidth={w * 0.78} />
          )}
        </g>
      ))}
    </g>
  </>
)

Desert.label = 'The sun over the Sonoran desert, drawn to match the time of day.'

export default Desert
